<div hlmCard [class.opacity-70]="isArchived">
  <div hlmCardContent class="flex gap-4">
    <!-- Image -->
    <div class="bg-muted relative size-24 shrink-0 overflow-hidden rounded-lg">
      @if (position !== undefined && !isArchived) {
        <span
          hlmBadge
          variant="solid"
          class="absolute start-0 top-0 z-10 rounded-none rounded-ee-md">
          {{ position }}
        </span>
      }

      @if (product.image_cropped_path || product.image_path) {
        <img
          [src]="getImageUrl(product.image_cropped_path || product.image_path, product.updated_at)"
          [alt]="product.name"
          class="size-full object-cover"
          [class.grayscale]="isArchived || !product.is_active" />
      } @else {
        <div class="text-muted-foreground flex size-full items-center justify-center">
          <ng-icon name="lucideImage" class="text-[length:--spacing(6)]" />
        </div>
      }
    </div>

    <!-- Name, price, description -->
    <div class="flex min-w-0 flex-1 flex-col gap-1">
      <div class="flex items-start justify-between gap-3">
        <p class="text-base leading-snug font-medium" [class.text-muted-foreground]="isArchived">
          {{ product.name }}
        </p>

        <div class="shrink-0 text-right">
          @if (hasDiscount) {
            <p class="text-muted-foreground text-xs leading-none line-through">
              {{ formatPrice(product.price) }}
            </p>
          }
          <p class="text-lg font-semibold tabular-nums">
            {{ formatPrice(hasDiscount ? discountedPrice : product.price) }}
          </p>
          @if (hasDiscount) {
            <p class="text-muted-foreground text-xs">
              −{{ product.discount_percentage }}% · Wert {{ formatPrice(product.price) }}
            </p>
          }
        </div>
      </div>

      @if (product.description) {
        <p class="text-muted-foreground line-clamp-2 text-sm whitespace-pre-line">
          {{ product.description }}
        </p>
      }
    </div>
  </div>

  <!-- Footer: state, sales, actions -->
  <div hlmCardFooter class="flex-row flex-wrap items-center gap-2 border-t pt-3">
    @if (isArchived) {
      <span hlmBadge variant="secondary">Archiviert</span>
    } @else {
      <span hlmBadge [variant]="product.is_active ? 'solid' : 'outline'">
        {{ product.is_active ? 'Aktiv' : 'Inaktiv' }}
      </span>
    }

    <span class="text-muted-foreground flex items-center gap-1 text-xs">
      <ng-icon name="lucideShoppingCart" />
      {{ product.purchase_count || 0 }} verkauft
    </span>

    <div class="flex-1"></div>

    @if (!isArchived) {
      <!-- Order -->
      <div class="flex items-center gap-0.5">
        <button
          hlmBtn
          variant="ghost"
          size="icon-sm"
          aria-label="Nach oben verschieben"
          [disabled]="isFirst || loading"
          (click)="onMoveLeft(); $event.stopPropagation()">
          <ng-icon name="lucideArrowUp" />
        </button>
        <button
          hlmBtn
          variant="ghost"
          size="icon-sm"
          aria-label="Nach unten verschieben"
          [disabled]="isLast || loading"
          (click)="onMoveRight(); $event.stopPropagation()">
          <ng-icon name="lucideArrowDown" />
        </button>
      </div>

      <!-- Dropdown on desktop, bottom sheet on phones. -->
      <button
        hlmBtn
        variant="outline"
        size="sm"
        class="hidden sm:inline-flex"
        [disabled]="loading || isDownloadingExample"
        [hlmDropdownMenuTrigger]="actionsMenu"
        align="end">
        @if (isDownloadingExample) {
          <hlm-spinner />
        } @else {
          <ng-icon name="lucideEllipsis" />
        }
        Aktionen
      </button>

      <hlm-sheet
        side="bottom"
        class="sm:hidden"
        [state]="actionsSheetOpen ? 'open' : 'closed'"
        (stateChanged)="actionsSheetOpen = $event === 'open'"
        (closed)="runPendingAction()">
        <button
          hlmSheetTrigger
          hlmBtn
          variant="outline"
          size="sm"
          [disabled]="loading || isDownloadingExample">
          @if (isDownloadingExample) {
            <hlm-spinner />
          } @else {
            <ng-icon name="lucideEllipsis" />
          }
          Aktionen
        </button>

        <hlm-sheet-content
          *hlmSheetPortal
          class="max-h-[85vh] gap-2 overflow-y-auto rounded-t-xl px-2 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div hlmSheetHeader class="px-3 pe-12">
            <h2 hlmSheetTitle class="truncate">{{ product.name }}</h2>
            <p hlmSheetDescription>Aktionen für dieses Produkt</p>
          </div>

          <nav class="flex flex-col">
            <button
              hlmBtn
              variant="ghost"
              class="h-12 justify-start gap-3 text-base"
              [disabled]="loading"
              (click)="chooseAction(onEdit)">
              <ng-icon name="lucidePencil" />
              Bearbeiten
            </button>
            <button
              hlmBtn
              variant="ghost"
              class="h-12 justify-start gap-3 text-base"
              [disabled]="loading"
              (click)="chooseAction(onToggleStatus)">
              <ng-icon [name]="product.is_active ? 'lucideEyeOff' : 'lucideEye'" />
              {{ product.is_active ? 'Deaktivieren' : 'Aktivieren' }}
            </button>

            <div hlmSeparator class="my-1"></div>

            <button
              hlmBtn
              variant="ghost"
              class="h-12 justify-start gap-3 text-base"
              [disabled]="loading"
              (click)="chooseAction(onViewVersionHistory)">
              <ng-icon name="lucideHistory" />
              Versionshistorie
              <span hlmBadge variant="secondary" class="ms-auto">
                {{ product.version_count || 1 }}
              </span>
            </button>
            <button
              hlmBtn
              variant="ghost"
              class="h-12 justify-start gap-3 text-base"
              [disabled]="loading"
              (click)="chooseAction(onDownloadExample)">
              <ng-icon name="lucideFileText" />
              Beispiel-Gutschein
            </button>

            <div hlmSeparator class="my-1"></div>

            <button
              hlmBtn
              variant="ghost"
              class="text-destructive hover:text-destructive hover:bg-destructive/10 h-12 justify-start gap-3 text-base"
              [disabled]="loading"
              (click)="chooseAction(onArchive)">
              <ng-icon name="lucideArchive" />
              Archivieren
            </button>
          </nav>
        </hlm-sheet-content>
      </hlm-sheet>
    } @else {
      <button hlmBtn variant="outline" size="sm" [disabled]="loading" (click)="onUnarchive()">
        <ng-icon name="lucideArchiveRestore" />
        Wiederherstellen
      </button>
    }
  </div>
</div>

<ng-template #actionsMenu>
  <hlm-dropdown-menu class="w-56">
    <hlm-dropdown-menu-group>
      <button hlmDropdownMenuItem [disabled]="loading" (click)="onEdit()">
        <ng-icon name="lucidePencil" />
        Bearbeiten
      </button>
      <button hlmDropdownMenuItem [disabled]="loading" (click)="onToggleStatus()">
        <ng-icon [name]="product.is_active ? 'lucideEyeOff' : 'lucideEye'" />
        {{ product.is_active ? 'Deaktivieren' : 'Aktivieren' }}
      </button>
    </hlm-dropdown-menu-group>

    <div hlmDropdownMenuSeparator></div>

    <hlm-dropdown-menu-group>
      <button hlmDropdownMenuItem [disabled]="loading" (click)="onViewVersionHistory()">
        <ng-icon name="lucideHistory" />
        Versionshistorie
        <span hlmBadge variant="secondary" class="ms-auto">{{ product.version_count || 1 }}</span>
      </button>
      <button hlmDropdownMenuItem [disabled]="loading" (click)="onDownloadExample()">
        <ng-icon name="lucideFileText" />
        Beispiel-Gutschein
      </button>
    </hlm-dropdown-menu-group>

    <div hlmDropdownMenuSeparator></div>

    <button hlmDropdownMenuItem variant="destructive" [disabled]="loading" (click)="onArchive()">
      <ng-icon name="lucideArchive" />
      Archivieren
    </button>
  </hlm-dropdown-menu>
</ng-template>
